import cjaAnalytics from 'yoda-core-components/lib/helpers/Analytics/CJAAnalytics';
import { triggerAddSaveItems } from 'yoda-core-components/lib/helpers/Utils/Utils';
import buildCJAPageViewPayload from 'yoda-core-components/lib/helpers/Analytics/CJAPageViewBuilder';
import buildCJAEvent from 'yoda-core-components/lib/helpers/Analytics/CJAEventBuilder';
import { buildProductItem } from 'yoda-core-components/lib/helpers/Analytics/CJAProductBuilder';
import { ADD_SAVEDITEMS } from 'yoda-site-components/lib/actionTypes/SavedItemActionTypes';
import { getModuleTrackingPageType } from 'yoda-site-components/lib/helpers/Utils/utils';
import {
    BOPIS_DEFAULT_STORE_CHANGE,
    DYNAMIC_GALLERIES_ITEM_CLICK,
    DYNAMIC_GALLERIES_MODULE_CLICK,
    DYNAMIC_VISNAV_CLICK,
    GALLERY_VIDEO_PLAYER_ANALYTICS,
    ON_PAGE_READY,
    PRODUCT_FILTERS_APPLIED,
    SEARCH_RESULT_CLICK,
    TRIGGER_STORE_SEARCH_EVENT,
} from '../actionTypes/AnalyticsActionTypes';
import { UPDATE_VIEW_TYPE } from '../actionTypes/ProductListActionTypes';
import { BOPIS_GALLERY_CHANGE_ZIP } from '../actionTypes/BOPISActionTypes';
import constants from '../analytics/Analytics.config';
import { getCategoryObj, setsbResultsInfo } from '../analytics/analytics.helper';
import {
    getPageType,
    getURLParameterBykey,
    getLastFilter,
    isFilterAnalytics,
    feedPageLoad,
    isPageLoad,
} from '../utils';
import getBreadCrumbInfo from '../selectors/BreadCrumbs';

const buildPageViewPayload = (state) => {
    const context = state.context || {};
    const pagename = getPageType();
    const isGalleryPage = pagename === 'g';
    const nullPage = isGalleryPage ? constants.PAGE_ng : constants.PAGE_ns;
    const pageType = state.countOfSearchResults ? constants[`PAGE_${pagename}`] : nullPage;
    const categoryObj = getCategoryObj(state, pageType);

    const breadcrumbs = getBreadCrumbInfo(state);
    const primary = categoryObj.primaryCategory || pageType;
    const sub1 = categoryObj.subCategory1 || '';
    const sub2 = categoryObj.subCategory2 || '';
    const breadcrumbPath = (breadcrumbs || [])
        .map((b) => b.breadCrumbLabel || '')
        .filter(Boolean)
        .join('>')
        .toLowerCase();

    return buildCJAPageViewPayload(
        {
            name: `d:${pageType}:${primary}`.toLowerCase(),
            type: pageType,
            breadcrumbs: breadcrumbPath,
            siteSection: primary,
            category: {
                primary,
                sub1,
                sub2,
            },
            flags: {
                isErrorPage: !!state.ErrorPage,
                isSponsorAdsDisplayed: !!(state.sponsoredDisplayPage && state.products?.length > 0),
            },
            tech: {
                releaseVersion: context.__RELEASEVERSION__ || '',
                // eslint-disable-next-line no-nested-ternary
                version: state.countOfSearchResults
                    ? `apollo 1 ${pageType}`
                    : isGalleryPage
                    ? constants.VERSION_ng
                    : constants.VERSION_ns,
            },
            sbResultsInfo: setsbResultsInfo(state),
        },
        {},
        {
            interactionName: isGalleryPage ? 'product_list_view' : 'view_search_results',
        }
    );
};

const buildLinkClickEvent = (params) =>
    buildCJAEvent({
        event: 'linkClick',
        event_name: 'linkClick',
        linkUrl: window?.location?.pathname || '',
        ...params,
    });

const buildSaveProductPayload = (product, productId, stateProduct) => ({
    event_name: 'save_product',
    products: [
        buildProductItem({
            id: productId,
            name: product.productName || stateProduct.name || '',
            link: product.productLink || stateProduct.pdpUrl || '',
            imgSource: product.productImageSrc || '',
            listPrice: stateProduct.originalMin || 0,
            listPriceRange: stateProduct.originalMax
                ? `${stateProduct.originalMin}-${stateProduct.originalMax}`
                : '',
            sellingPrice: stateProduct.currentMin || 0,
            sellingPriceRange: stateProduct.currentMax
                ? `${stateProduct.currentMin}-${stateProduct.currentMax}`
                : '',
            pricingCadence: stateProduct.currentPriceLabel || '',
            inventoryStatus: stateProduct.channelMessage || '',
            averageRating: stateProduct.averageRating || 0,
            review: stateProduct.reviewCount || 0,
            SKU: stateProduct.skuId || '',
            brand: stateProduct.brand || '',
            flags: {
                isEcoRebate: !!stateProduct.ecoRebate,
                isSponsored: !!stateProduct.isSponsored,
            },
        }),
    ],
});

const CJAMiddleware = (store) => (next) => (action) => {
    if (typeof window === 'undefined') return next(action);
    if (!cjaAnalytics.isEnabled()) return next(action);

    const state = store.getState();
    let eventName = null;
    let eventData = null;

    switch (action.type) {
        case ON_PAGE_READY: {
            eventName = 'pageView';
            eventData = buildPageViewPayload(state);

            if (
                getURLParameterBykey('searchType')?.match(/predictive/i) &&
                getURLParameterBykey('brand')
            ) {
                cjaAnalytics.track(
                    'linkClick',
                    buildLinkClickEvent({
                        linkRegion: 'gallery',
                        linkComponent: 'store analytics',
                        linkId: 'predictive-search-brand-filter',
                        linkModuleName: 'predictive search with brand filter',
                    })
                );
            }
            break;
        }
        case ADD_SAVEDITEMS: {
            if (triggerAddSaveItems(state)) {
                const product = action.payload?.[0]?.product || {};
                const productId = product.id || '';
                const stateProduct = (state.products || []).find((p) => p.ppId === productId) || {};
                eventName = 'save_product';
                eventData = buildSaveProductPayload(product, productId, stateProduct);
            }
            break;
        }
        case SEARCH_RESULT_CLICK: {
            const { sbResultsInfo = {} } = action.payload || {};
            const productInfo = sbResultsInfo?.product?.[0]?.productInfo || {};
            const ppId = productInfo.productPPID || '';
            eventData = {
                event: 'search_results_click',
                event_name: 'search_results_click',
                searchTerm: getURLParameterBykey('Ntt') || getURLParameterBykey('searchTerm') || '',
                searchType: getURLParameterBykey('searchType') || '',
                searchLinkText: productInfo.productName || ppId,
                searchLinkHref: productInfo.productLink || '',
                searchClickPosition: parseInt(sbResultsInfo.resultPosition, 10) || 0,
                searchLinkId: ppId,
            };
            break;
        }
        case DYNAMIC_GALLERIES_ITEM_CLICK: {
            const { dynamicGalleryID = '', dynamicGalleryPPID = '' } = action.payload || {};
            eventData = buildLinkClickEvent({
                linkRegion: 'product grid zone',
                linkComponent: 'dynamic galleries widget',
                linkId: 'dynamic-galleries-widget-container',
                linkText: 'dynamic gallery',
                linkModuleName: `dynamicGallery:${dynamicGalleryID}:${dynamicGalleryPPID}`,
            });
            break;
        }
        case DYNAMIC_GALLERIES_MODULE_CLICK: {
            const { dynamicGalleryID = '' } = action.payload || {};
            eventData = buildLinkClickEvent({
                linkRegion: 'product grid zone',
                linkComponent: 'dynamic galleries bundle',
                linkId: dynamicGalleryID,
                linkText: 'bundle',
                linkModuleName: `dynamicGallery:${dynamicGalleryID}`,
            });
            break;
        }
        case DYNAMIC_VISNAV_CLICK: {
            const { dynamicVisualMapPositionClicked = '' } = action.payload || {};
            eventData = buildLinkClickEvent({
                linkRegion: 'product grid zone',
                linkComponent: 'dynamic vis nav',
                linkId: dynamicVisualMapPositionClicked,
                linkText: dynamicVisualMapPositionClicked,
                linkModuleName: 'dynamicVisNav',
            });
            break;
        }
        case PRODUCT_FILTERS_APPLIED: {
            let lastFilter = getLastFilter();
            const promotedFilter = state.promotedFilterSelected || {};
            if (promotedFilter.value === 'interactivevisnav') {
                lastFilter = {
                    action: 'add',
                    name: 'interactivevisnav',
                    value: promotedFilter.heading,
                };
            } else if (promotedFilter.value === 'suggestedfilter') {
                lastFilter = {
                    action: 'add',
                    name: 'suggestedfilter',
                    value: promotedFilter.heading,
                };
            }
            const selectedFilters = state.selectedFilters?.length || 0;
            const selectedStores = state.bopisInfo?.selectedStores || [];
            if ((selectedFilters || lastFilter) && isFilterAnalytics()) {
                const filterAction = lastFilter?.action || '';
                const pageType = getPageType();
                const sbResultsInfo =
                    setsbResultsInfo(state, lastFilter, selectedStores, isPageLoad()) || {};
                const categoryObj = getCategoryObj(state, pageType) || {};
                eventData = buildCJAEvent({
                    event: 'productfiltersapplied',
                    event_name: 'productfiltersapplied',
                    linkComponent: 'StoreAnalytics',
                    linkId: 'filter-panel',
                    linkText: filterAction,
                    linkType: 'other',
                    linkUrl: window?.location?.pathname || '',
                    linkModuleName: `${getModuleTrackingPageType(
                        window.location.pathname
                    )}:productfilters`,
                    linkRegion: 'filter',
                    linkPosition: 0,
                    searchTerm: sbResultsInfo.searchTerm || '',
                    defaultSearchTerm: sbResultsInfo.defaultSearchTerm || '',
                    searchType: sbResultsInfo.typeOfSearch || '',
                    searchCount: state.countOfSearchResults || 0,
                    resutlsPageNumber: Number(sbResultsInfo.resultsPageNumber) || 0,
                    resultsPerPage: Number(sbResultsInfo.resultsPerPage) || 0,
                    sortByType: sbResultsInfo.sortByType || '',
                    viewType: sbResultsInfo.viewType || '',
                    searchDepartmentName: categoryObj.primaryCategory || '',
                    sortSearchStrategy: sbResultsInfo.sortSearchStrategy || '',
                    searchFilter: sbResultsInfo.filters || [],
                });
                feedPageLoad();
            }
            break;
        }
        case BOPIS_GALLERY_CHANGE_ZIP:
            eventData = buildLinkClickEvent({
                linkRegion: 'filter panel',
                linkComponent: 'find a store',
                linkId: 'bopisChangeZip',
                linkText: 'find stores',
                linkModuleName: 'bopisChangeZip',
            });
            break;
        case UPDATE_VIEW_TYPE: {
            if (action.payload?.userAction) {
                const viewType = (action.payload.viewType || '').toLowerCase();
                eventData = buildLinkClickEvent({
                    linkRegion: 'product toolbar',
                    linkComponent: 'product list settings',
                    linkId: viewType,
                    linkText: `${viewType} view`,
                    linkModuleName: `switchPageOrientation:${viewType}`,
                });
            }
            break;
        }
        case GALLERY_VIDEO_PLAYER_ANALYTICS: {
            const { videoPlayerData, product } = action.payload || {};
            const ppId = product?.[0]?.productInfo?.productPPID || '';
            eventName = 'impressions';
            eventData = {
                event_name: 'impressions',
                impressionId: ppId,
                impressionName: videoPlayerData?.id?.() || '',
                impressionType: 'video interaction',
            };
            break;
        }
        case TRIGGER_STORE_SEARCH_EVENT: {
            const { searchText = '' } = action;
            eventData = buildLinkClickEvent({
                linkRegion: 'filter panel',
                linkComponent: 'find a store',
                linkId: 'search-by-zipcode',
                linkText: searchText,
                linkModuleName: `storeSearch:${searchText}`,
            });
            break;
        }
        case BOPIS_DEFAULT_STORE_CHANGE:
            eventData = buildLinkClickEvent({
                linkRegion: 'filter panel',
                linkComponent: 'stores quick info',
                linkId: 'at-change-stores-list',
                linkModuleName: 'bopisDefaultStoreChange',
            });
            break;
        default:
            break;
    }

    if (eventData) {
        cjaAnalytics.track(eventName || eventData.event, eventData);
    }

    return next(action);
};

export default CJAMiddleware;
